import { apiRequest } from "../lib/apiClient";
import { API_BASE_URL } from "../env";
import type {
  BarcodeCreate,
  BarcodeOut,
  InventoryImportResult,
  InventoryItemCreateExt,
  InventoryItemOutExt,
  InventoryItemUpdateExt,
  SerializedUnitCreate,
  SerializedUnitOut,
  StockLogOut,
  WriteOffOut,
  WriteOffRequest,
} from "./types";

// This module standardizes on the `*Ext` shapes (tax_rate_id/is_serialized, added by the
// 2026-07-13 integration pass — see `types.ts`) for every inventory item request/response;
// they're a strict superset of the pre-integration `InventoryItemOut`/`Create`/`Update`.

export async function listInventory(accessToken: string, branchId?: string): Promise<InventoryItemOutExt[]> {
  return apiRequest<InventoryItemOutExt[]>("/inventory", { accessToken, query: { branch_id: branchId } });
}

export async function getInventoryItem(accessToken: string, itemId: string): Promise<InventoryItemOutExt> {
  return apiRequest<InventoryItemOutExt>(`/inventory/${itemId}`, { accessToken });
}

export async function createInventoryItem(accessToken: string, body: InventoryItemCreateExt): Promise<InventoryItemOutExt> {
  return apiRequest<InventoryItemOutExt>("/inventory", { method: "POST", accessToken, body });
}

/** `PATCH /inventory/{id}` — the absolute-value admin edit path. `body.version` must be
 * the `version` most recently read for this item; a stale value gets a 409 from the
 * backend (translated to `ApiError` with `status === 409` by `apiClient`), never a forced
 * overwrite (`backend/BACKEND_ARCHITECTURE.md` §5). Callers should catch that specifically
 * and prompt a re-fetch rather than retrying blindly. */
export async function updateInventoryItem(
  accessToken: string,
  itemId: string,
  body: InventoryItemUpdateExt,
): Promise<InventoryItemOutExt> {
  return apiRequest<InventoryItemOutExt>(`/inventory/${itemId}`, { method: "PATCH", accessToken, body });
}

export async function getStockLogs(accessToken: string, itemId: string): Promise<StockLogOut[]> {
  return apiRequest<StockLogOut[]>(`/inventory/${itemId}/stock-logs`, { accessToken });
}

/** `GET /inventory/lookup?code=` — resolves either an exact `sku` match or a
 * `Barcode.code` match (§10.2's multi-barcode support: a product can carry more than one
 * code, e.g. unit vs. case pack). 404 if nothing matches. */
export async function lookupInventoryItem(accessToken: string, code: string): Promise<InventoryItemOutExt> {
  return apiRequest<InventoryItemOutExt>("/inventory/lookup", { accessToken, query: { code } });
}

/** `GET /inventory/{id}/barcodes` — genuinely exists (confirmed 2026-07-15) despite this
 * file/`InventoryDetailPage.tsx` previously claiming otherwise and keeping barcodes in
 * session-only state as a result; now actually called. */
export async function listBarcodes(accessToken: string, itemId: string): Promise<BarcodeOut[]> {
  return apiRequest<BarcodeOut[]>(`/inventory/${itemId}/barcodes`, { accessToken });
}

export async function addBarcode(accessToken: string, itemId: string, body: BarcodeCreate): Promise<BarcodeOut> {
  return apiRequest<BarcodeOut>(`/inventory/${itemId}/barcodes`, { method: "POST", accessToken, body });
}

export async function deleteBarcode(accessToken: string, itemId: string, barcodeId: string): Promise<void> {
  await apiRequest<void>(`/inventory/${itemId}/barcodes/${barcodeId}`, { method: "DELETE", accessToken });
}

/** `POST /inventory/{id}/write-off` — records an `EXPIRED_WASTE`-reasoned `StockLog`
 * with a required reason/note and decrements stock via the same guarded-delta pattern
 * live sales use (never negative unless the item explicitly allows backorder). This is
 * the real "this batch spoiled" action — distinct from a silent manual PATCH that would
 * leave no reason on record. */
export async function writeOffInventoryItem(accessToken: string, itemId: string, body: WriteOffRequest): Promise<WriteOffOut> {
  return apiRequest<WriteOffOut>(`/inventory/${itemId}/write-off`, { method: "POST", accessToken, body });
}

/** `POST /inventory/import` — OWNER/BRANCH_MANAGER/ECOM_MGR, multipart. `branch_id` is a
 * query param on the backend (NOT a form field, unlike bank-reconciliation's import),
 * `file` is the multipart body. Row-level error reporting — one bad row never fails the
 * whole batch (§14.2). */
export async function importInventoryCsv(accessToken: string, branchId: string, file: File): Promise<InventoryImportResult> {
  const formData = new FormData();
  formData.set("file", file);
  return apiRequest<InventoryImportResult>("/inventory/import", {
    method: "POST",
    accessToken,
    query: { branch_id: branchId },
    formData,
  });
}

/** `GET /inventory/export` — any authenticated user. Returns a raw CSV `Response`, not
 * JSON, so this bypasses `apiRequest`'s JSON parsing and returns a `Blob` for the caller
 * to trigger a download from. */
export async function exportInventoryCsv(accessToken: string, branchId?: string): Promise<Blob> {
  const url = new URL(`${API_BASE_URL}/inventory/export`);
  if (branchId) url.searchParams.set("branch_id", branchId);
  const response = await fetch(url.toString(), { headers: { Authorization: `Bearer ${accessToken}` } });
  if (!response.ok) throw new Error(`Export failed with status ${response.status}`);
  return response.blob();
}

/** `POST /inventory/{id}/serial-units` — OWNER/BRANCH_MANAGER/ECOM_MGR. Registers a batch
 * of serial numbers as `IN_STOCK`, incrementing `current_stock` by the count (§14.4) —
 * typically used right after receiving a PO for a serialized item. */
export async function registerSerialUnits(accessToken: string, itemId: string, body: SerializedUnitCreate): Promise<SerializedUnitOut[]> {
  return apiRequest<SerializedUnitOut[]>(`/inventory/${itemId}/serial-units`, { method: "POST", accessToken, body });
}

/** `GET /inventory/{id}/serial-units?status=` — any authenticated user. */
export async function listSerialUnits(accessToken: string, itemId: string, status?: string): Promise<SerializedUnitOut[]> {
  return apiRequest<SerializedUnitOut[]>(`/inventory/${itemId}/serial-units`, { accessToken, query: { status } });
}
